import type {
  ApiDashboardElement,
  ApiSourceLabelPair,
  SourceLabelScope,
  SourceMap,
} from '@highimpact-frontend/api-types';

import type { SourceLabelsApi } from '../api/DashboardHostApi';
import { toSourceLabelKey } from '../api/sourceLabelKeys';
import {
  elementNeedsSourceLabelResolution,
  flattenDashboardElements,
  getMeasurePointPathsFromElement,
} from './hydrationSourceLabels';

export const EMPTY_SOURCE_MAP: SourceMap = {};

/** @deprecated Use {@link EMPTY_SOURCE_MAP}. */
export const EMPTY_SOURCE_LABEL_MAP = EMPTY_SOURCE_MAP;

const dedupePairs = (pairs: readonly ApiSourceLabelPair[]): ApiSourceLabelPair[] => {
  const seen = new Set<string>();

  return pairs.filter((pair) => {
    const key = toSourceLabelKey(pair.measure_point_id, pair.measure_type_id);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** Collects unique measure-point pairs that still need label resolution. */
export const collectSourceLabelPairsFromElements = (
  elements: readonly ApiDashboardElement[]
): ApiSourceLabelPair[] => {
  const pairs = flattenDashboardElements(elements).flatMap((element) => {
    if (!elementNeedsSourceLabelResolution(element)) {
      return [];
    }

    return getMeasurePointPathsFromElement(element);
  });

  return dedupePairs(pairs);
};

/** Batch-resolves sources for dashboard elements via the host labels API. */
export const fetchSourceLabelMapForElements = async (
  getLabels: SourceLabelsApi['getLabels'],
  elements: readonly ApiDashboardElement[],
  scope?: SourceLabelScope
): Promise<SourceMap> => {
  const pairs = collectSourceLabelPairsFromElements(elements);

  if (pairs.length === 0) {
    return EMPTY_SOURCE_MAP;
  }

  return getLabels(pairs, scope);
};
